import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button, TextField } from '../../components/ui'
import { keys } from '../../api/keys'
import { saveTerms } from '../../api/objects-write'
import type { Listing } from '../../api/objects'
import { t } from '../../i18n'

/**
 * What somebody read off the contract.
 *
 * This is the missing half of a rule that was already enforced: the matcher
 * refuses to offer a flat whose contract type nobody has recorded, because no
 * legal conclusion is ours to draw. Until this form existed there was nowhere to
 * record one — so every object entered by hand was withheld from every client
 * for a fact the panel could not supply, and the screen said nothing about it.
 *
 * The two boolean fields keep three answers. "не зарегистрирован" and "никто не
 * спрашивал" lead to different conversations with a client, and a checkbox
 * cannot tell them apart.
 */
export function TermsForm({
  listing,
  propertyID,
}: {
  readonly listing: Listing
  readonly propertyID: string
}) {
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const [contract, setContract] = useState(listing.contractTypeDeclared ?? '')
  const [registered, setRegistered] = useState(triState(listing.contractRegistered))
  const [residenza, setResidenza] = useState(triState(listing.residenzaPossible))

  function reset() {
    setContract(listing.contractTypeDeclared ?? '')
    setRegistered(triState(listing.contractRegistered))
    setResidenza(triState(listing.residenzaPossible))
    setFailure(null)
  }

  const save = useMutation({
    mutationFn: () =>
      saveTerms(listing.id, {
        contract_type_declared: contract.trim(),
        ...boolOrClear('contract_registered', 'clear_contract_registered', registered),
        ...boolOrClear('residenza_possible', 'clear_residenza', residenza),
      }),
    onSuccess: () => {
      setOpen(false)
      setFailure(null)
      void client.invalidateQueries({ queryKey: keys.property(propertyID) })
    },
    onError: (error: unknown) => {
      setFailure(error instanceof Error ? error.message : t('build.failed'))
    },
  })

  if (!open) {
    return (
      <div className="terms">
        {/*
          Said plainly, because it is the difference between a flat that can be
          offered and one that silently cannot.
        */}
        {listing.contractTypeDeclared === undefined ? (
          <Alert tone="warning">{t('listing.contractMissing')}</Alert>
        ) : null}
        <Button
          variant="ghost"
          onClick={() => {
            reset()
            setOpen(true)
          }}
        >
          {listing.contractTypeDeclared === undefined
            ? t('listing.contractSet')
            : t('listing.contractEdit')}
        </Button>
      </div>
    )
  }

  return (
    <form
      className="build-form"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <TextField
        label={t('listing.contractType')}
        value={contract}
        placeholder={t('listing.contractTypePlaceholder')}
        onChange={(event) => {
          setContract(event.target.value)
        }}
      />

      <div className="build-form__row">
        <ThreeWay
          label={t('listing.contractRegistered')}
          value={registered}
          onChange={setRegistered}
        />
        <ThreeWay label={t('listing.residenza')} value={residenza} onChange={setResidenza} />
      </div>

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}

      <div className="build-form__actions">
        <Button type="submit" busy={save.isPending}>
          {save.isPending ? t('build.saving') : t('build.save')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            reset()
            setOpen(false)
          }}
          disabled={save.isPending}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}

function ThreeWay({
  label,
  value,
  onChange,
}: {
  readonly label: string
  readonly value: string
  readonly onChange: (next: string) => void
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <select
        className="field__input"
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      >
        <option value="">{t('objects.notAsked')}</option>
        <option value="yes">{t('common.yes')}</option>
        <option value="no">{t('common.no')}</option>
      </select>
    </label>
  )
}

function triState(value: boolean | undefined): string {
  return value === undefined ? '' : value ? 'yes' : 'no'
}

/** Absent leaves it, a value sets it, and the flag empties it back to "nobody
 * has said" — the third answer a boolean cannot carry. */
function boolOrClear(field: string, clear: string, value: string): Record<string, unknown> {
  if (value === '') return { [clear]: true }
  return { [field]: value === 'yes' }
}
